import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPhone, FiMail, FiMenu, FiX } from 'react-icons/fi';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.scrolled ? 'rgba(26, 77, 128, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(10px)' : 'none'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.scrolled ? '0 2px 20px rgba(0,0,0,0.1)' : 'none'};
`;

const TopBar = styled.div`
  background: white;
  color: ${props => props.theme.colors.primary};
  padding: 8px 0;
  font-size: 0.9rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const TopBarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  
  span {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const MainHeader = styled.div`
  padding: 15px 0;
  max-width: 1200px;
  margin: 0 auto;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1001;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  
  img {
    height: 60px;
    width: auto;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    img {
      height: 50px;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 35px;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.theme.colors.white};
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  position: relative;
  padding: 8px 0;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
    transform: translateY(-2px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 3px;
    background: ${props => props.theme.colors.secondary};
    transition: width 0.3s ease;
  }
  
  &:hover::after,
  &.active::after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.white};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px;
  z-index: 1002;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.colors.primary};
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 0;
  overflow: visible;
`;

const MobileNavbar = styled.nav`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0rem;
  width: 100%;
  background: ${props => props.theme.colors.primary};
  min-height: 80px;
`;

const MobileNavbarBrand = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  text-transform: uppercase;
  color: ${props => props.theme.colors.white};
  font-weight: bold;
  
  img {
    height: 50px;
    width: auto;
  }
`;

const MobileNavbarToggler = styled.button`
  display: block;
  background: none;
  border: none;
  color: ${props => props.theme.colors.white};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px;
  z-index: 10002;
  
  @media (min-width: 992px) {
    display: none;
  }
`;

const MobileNavbarCollapse = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  padding: 20px;
  flex: 1;
  min-height: calc(100vh - 80px);
  background: ${props => props.theme.colors.primary};
`;

const MobileNavbarNav = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 0;
  background: ${props => props.theme.colors.primary};
`;

const MobileNavItem = styled.li`
  width: 100%;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: ${props => props.theme.colors.primary};
  
  &:last-child {
    border-bottom: none;
  }
`;

const MobileNavLink = styled(Link)`
  color: ${props => props.theme.colors.white};
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: 600;
  padding: 20px 15px;
  text-align: center;
  width: 100%;
  transition: all 0.3s ease;
  display: block;
  background: ${props => props.theme.colors.primary};
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
    background: rgba(255, 255, 255, 0.1);
    text-decoration: none;
  }
  
  &.active {
    color: ${props => props.theme.colors.secondary};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const MobileContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 30px;
  text-align: center;
  background: ${props => props.theme.colors.primary};
  width: 100%;
  max-width: 400px;
  
  p {
    color: ${props => props.theme.colors.white};
    margin: 0;
    font-size: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: ${props => props.theme.colors.primary};
  }
  
  svg {
    color: ${props => props.theme.colors.secondary};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 30px;
  right: 30px;
  background: none;
  border: none;
  color: ${props => props.theme.colors.white};
  font-size: 2.5rem;
  cursor: pointer;
  padding: 10px;
  transition: all 0.3s ease;
  z-index: 10001;
  
  &:hover {
    color: ${props => props.theme.colors.secondary};
    transform: scale(1.1);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    top: 20px;
    right: 20px;
    font-size: 2rem;
  }
`;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/what-we-do', label: 'What We Do' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/contact', label: 'Contact Us' }
  ];

  return (
    <HeaderContainer scrolled={scrolled}>
      <TopBar>
        <TopBarContent>
          <ContactInfo>
            <span>
              <FiPhone />
              +91 88808 28698
            </span>
            <span>
              <FiMail />
              suryakiran1321@gmail.com
            </span>
          </ContactInfo>
        </TopBarContent>
      </TopBar>
      
      <MainHeader>
        <Logo to="/">
          <img src="/logo-simple.svg" alt="Trimurthy Tent House" />
        </Logo>
        
        <Nav>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={location.pathname === item.path ? 'active' : ''}
            >
              {item.label}
            </NavLink>
          ))}
        </Nav>
        
        <MobileMenuButton onClick={() => setMobileMenuOpen(true)}>
          <FiMenu />
        </MobileMenuButton>
      </MainHeader>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <MobileNavbar>
              <MobileNavbarBrand to="/">
                <img src="/logo-simple.svg" alt="Trimurthy Tent House" />
              </MobileNavbarBrand>
              <MobileNavbarToggler onClick={() => setMobileMenuOpen(false)}>
                <FiX />
              </MobileNavbarToggler>
            </MobileNavbar>

            <MobileNavbarCollapse>
              <MobileNavbarNav>
                {navItems.map((item, index) => (
                  <MobileNavItem key={item.path}>
                    <MobileNavLink
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={location.pathname === item.path ? 'active' : ''}
                    >
                      {item.label}
                    </MobileNavLink>
                  </MobileNavItem>
                ))}
              </MobileNavbarNav>
            </MobileNavbarCollapse>
          </MobileMenu>
        )}
      </AnimatePresence>
    </HeaderContainer>
  );
};

export default Header; 